import React from 'react';
import { push } from 'redux-first-history';
import { FormattedMessage } from 'react-intl';

import { logout } from 'modules/auth/actions';
import { useDispatch, useSelector } from 'hooks';
import { getSearch } from 'modules/router/selectors';
import { setFlowType } from 'modules/selfServices/actions';
import { getRedirectUrl } from 'modules/selfServices/selectors';
import { FlowType as FlowTypeEnum } from 'modules/selfServices/types/SelfService';

import Icon from './Icon';
import Modal from './Modal';
import Button, { ButtonType } from './Button';

const TYPES = {
  [FlowTypeEnum.CLASSIC]: {
    title: <FormattedMessage id="flowType.types.CLASSIC.title" defaultMessage="Check-in with questions" />,
    content: <FormattedMessage id="flowType.types.CLASSIC.content" defaultMessage="Answer a few questions to help us better prepare for the appointment, then drop the keys." />,
  },
  [FlowTypeEnum.QUICK]: {
    title: <FormattedMessage id="flowType.types.QUICK.title" defaultMessage="Quick check-in" />,
    content: <FormattedMessage id="flowType.types.QUICK.content" defaultMessage="Simply drop off the keys in just a few seconds." />,
  },
};

const FlowTypeModal: React.FC = () => {
  const dispatch = useDispatch();

  const search = useSelector(getSearch);
  const redirectUrl = useSelector(getRedirectUrl);

  const handleLogout = React.useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleClick = React.useCallback((type: FlowTypeEnum) => () => {
    dispatch(setFlowType(type));
    dispatch(push(`${redirectUrl}${search}`));
  }, [dispatch, search, redirectUrl]);

  return (
    <Modal
      open
      className="modal-large"
      outsideClickClose={false}
      title={<FormattedMessage id="flowType.title" defaultMessage="Start check-in" tagName="h2" />}
    >
      <div className="flex flex-col">
        {Object.entries(TYPES).map(([type, data]) => (
          <div
            key={type}
            className="flex items-center p-6 gap-3 rounded-card bg-card-radio-bg border-card-radio-size border-card-radio-border cursor-pointer my-2xs"
            onClick={handleClick(type as FlowTypeEnum)}
            data-testid={type}
          >
            <div className="flex-1">
              <div className="flex flex-col flex-grow gap-2">
                <span className="font-bold text-base kiosk:text-3xl">{data.title}</span>
              </div>
              <span className="text-sm kiosk:text-xl">{data.content}</span>
            </div>
            <Icon className="fb-icon--arrow-right text-2xl kiosk:text-4xl text-default" />
          </div>
        ))}
      </div>
      <Button onClick={handleLogout} className="w-full mt-3xs" type={ButtonType.SECONDARY} testId="logout">
        <FormattedMessage id="socket.updateLogoutModal.logOut" defaultMessage="Logout" values={{ countdown: 0 }} />
      </Button>
    </Modal>
  );
};

export default FlowTypeModal;
